/**
 * Transform API data into chart-compatible format
 * @param {Object} apiData - Data from the API response
 * @returns {Object} Formatted data for charts
 */
export const transformChartData = (apiData) => {
  if (!apiData || !apiData.columns || !apiData.rows) {
    return null;
  }

  const { columns, rows, metadata, column_display_names, column_value_types, chart_config } = apiData;
  
  // Transform rows into objects with column names as keys
  const transformedData = rows.map(row => {
    const dataPoint = {};
    columns.forEach((column, index) => {
      dataPoint[column] = row[index];
    });
    return dataPoint;
  });
  
  // chart_config is now required, but provide fallback for safety
  if (!chart_config) {
    console.warn('chart_config is missing from API response');
    return null;
  }
  
  const dataFormat = chart_config.data_format;
  const chartType = chart_config.chart_type;
  
  // Build chart types array: use alternative_chart_types if available, otherwise build from chart_type
  let chartTypes;
  if (chart_config.alternative_chart_types && chart_config.alternative_chart_types.length > 0) {
    chartTypes = [chartType, ...chart_config.alternative_chart_types];
  } else {
    // Fallback: primary chart type + table (only if not already table)
    chartTypes = chartType === 'table' ? ['table'] : [chartType, 'table'];
  }
  
  // Derive seriesInfo from chart_config.long_format_series if available
  const seriesInfo = chart_config.long_format_series ? {
    column: chart_config.long_format_series.column,
    name: chart_config.long_format_series.name || chart_config.long_format_series.label
  } : null;
  
  return {
    data: transformedData,
    columns,
    dataType: dataFormat,
    chartTypes: chartTypes,
    seriesInfo: seriesInfo,
    metadata: metadata || null,
    columnDisplayNames: column_display_names || null,
    columnValueTypes: column_value_types || null,
    chartConfig: chart_config
  };
};

/**
 * Format value based on its type from backend metadata with fallback
 * @param {number} value - Raw value
 * @param {string} columnName - Name of the column
 * @param {Object} columnValueTypes - Mapping of column names to value types (can be null/undefined)
 * @param {boolean} compact - Whether to use compact format
 * @returns {string} Formatted value
 */
export const formatValueByType = (value, columnName, columnValueTypes, compact = false) => {
  // Convert string numbers to actual numbers (database Decimal objects may serialize as strings)
  if (typeof value === 'string' && !isNaN(value) && value !== '') {
    value = parseFloat(value);
  }
  
  if (typeof value !== 'number' || isNaN(value)) return value;
  
  // Get value type from backend metadata, or fall back to count (safer default than currency)
  const valueType = columnValueTypes?.[columnName] || 'count';
  
  switch (valueType) {
    case 'hours':
      // Format as hours with 1 decimal (7.5 -> 7,5 t)
      return new Intl.NumberFormat('no-NO', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value) + '\u00A0t';

    case 'currency_per_hour':
      // Format as currency per hour (850 -> 850 kr/t)
      if (compact) {
        return formatFinancialValueCompact(value, 'financial') + '/t';
      }
      return formatFinancialValue(value, 'financial') + '/t';

    case 'percentage':
      // Format as percentage (0.56 -> 56.0%)
      return `${(value * 100).toFixed(1)}%`;
    
    case 'ratio':
      // Format as dimensionless ratio with 2 decimal places (e.g. -2.34, 1.50)
      return new Intl.NumberFormat('no-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

    case 'decimal':
      // Format as decimal with 2 places (37.5 -> 37,50)
      return new Intl.NumberFormat('no-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    
    case 'integer':
    case 'count':
      // Format as whole number (1234 -> 1 234)
      return new Intl.NumberFormat('no-NO', {
        maximumFractionDigits: 0
      }).format(value);
    
    case 'currency':
    default:
      // Default to currency formatting (1000 -> 1 000 kr)
      if (compact) {
        return formatFinancialValueCompact(value, 'financial');
      }
      return formatFinancialValue(value, 'financial');
  }
};

/**
 * Format financial values for display
 * @param {number} value - Raw value
 * @param {string} dataType - Type of data
 * @returns {string} Formatted value
 */
/**
 * Format financial values in compact format for Y-axis (80K, 10M)
 * @param {number} value - Raw value
 * @param {string} dataType - Type of data
 * @returns {string} Formatted compact value
 */
export const formatFinancialValueCompact = (value, dataType = 'financial') => {
  if (typeof value !== 'number') return value;
  
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  // Use compact format for large numbers (with non-breaking space to prevent line breaks)
  if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toFixed(1)}M\u00A0kr`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(0)}K\u00A0kr`;
  }
  
  // For small numbers with financial dataType, use currency format
  if (dataType === 'financial' || dataType === 'financial_ratio') {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  // For small non-financial numbers, use standard number format
  return new Intl.NumberFormat('no-NO').format(value);
};

/**
 * Format financial values in full format for tables and tooltips
 * @param {number} value - Raw value
 * @param {string} dataType - Type of data
 * @returns {string} Formatted full value
 */
export const formatFinancialValue = (value, dataType = 'financial') => {
  if (typeof value !== 'number') return value;
  
  if (dataType === 'financial' || dataType === 'financial_ratio') {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  return new Intl.NumberFormat('no-NO').format(value);
};

/**
 * Format a step label for the multi-graph selector dropdown.
 * Uses data_sources from the step's metadata; falls back to "Graph N".
 * @param {Array|null} dataSources - Array of table/source names
 * @param {number} tabIdx - Zero-based tab index (used for fallback label)
 * @param {Function} t - i18next translation function
 * @returns {string} Human-readable label
 */
export const formatStepLabel = (dataSources, tabIdx, t) => {
  if (!dataSources || dataSources.length === 0) {
    return `${t('charts.graphFallbackLabel')} ${tabIdx + 1}`;
  }
  const toTitle = (s) =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  if (dataSources.length === 1) return toTitle(dataSources[0]);
  if (dataSources.length === 2) return `${toTitle(dataSources[0])} & ${toTitle(dataSources[1])}`;
  const extra = dataSources.length - 2;
  return `${toTitle(dataSources[0])}, ${toTitle(dataSources[1])} ${t('charts.andMore', { count: extra })}`;
};

/**
 * Check if data is suitable for a specific chart type
 * @param {Array} data - Chart data
 * @param {string} chartType - Type of chart
 * @returns {boolean} True if suitable
 */
